/**
 * «Armar en el oficial» (29/10/2026): desde Nueva campaña (la segmentación) se llega a `campanas/nueva?analisis=<id>`, y
 * cada vez nacía otro borrador con los mismos grupos. Ahora, antes de mostrar «A quién», se buscan las campañas del
 * oficial de esa campaña de Activa (`GET /campanas?campanaActivaId=`, `delAnalisis`):
 * - con uno o más borradores, se muestran antes de «A quién» (`elegir`, `ElegirDelAnalisis`): cada uno con «Seguir
 *   armando», y «Armar otra» (`armarOtra`) sigue a «A quién» de siempre. No se abre ninguno solo: el operador arma una
 *   campaña por grupo, cada una con su plantilla y su cupón, y abrir el borrador que había no dejaba armar la de otros
 *   grupos (Joaquín y su borrador «Campeones», 29/10/2026);
 * - sin borradores, o con «Armar otra», el borrador nace en «A quién» como siempre, con `{tipo: "activa",
 *   campanaActivaId}`, al tocar «Siguiente» y no antes. Si ya salieron otras con esa audiencia (o están programadas), se
 *   avisan todas con sus grupos, sin frenar: puede querer otra;
 * - si no se puede saber, no se arma otra a ciegas: «Siguiente» espera y se puede reintentar.
 */

import { useEffect, useState } from "react";
import type { CampanaResumen } from "../../types";
import { oficialApi } from "../../api";
import { delAnalisis } from "../../logic/asistente";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";

export const TEXTO_NO_SE_PUDO_BUSCAR = "No pudimos revisar si ya hay una campaña del oficial con esta segmentación.";

type Estado = { buscando: boolean; borradores: CampanaResumen[]; salieron: CampanaResumen[]; error: string; otra: boolean };
const BUSCANDO: Estado = { buscando: true, borradores: [], salieron: [], error: "", otra: false };

export function useBorradorDelAnalisis({ idRuta, analisisUrl }: { idRuta: string | undefined; analisisUrl: string }) {
  const buscar = !idRuta && !!analisisUrl;
  const [estado, setEstado] = useState<Estado>({ ...BUSCANDO, buscando: buscar });
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    if (!buscar) return;
    let vivo = true;
    setEstado(BUSCANDO);
    (async () => {
      try {
        const l = leerRespuesta<CampanaResumen[]>(await oficialApi.campanasDelAnalisis(analisisUrl));
        if (!vivo) return;
        if (!l.ok) { setEstado({ ...BUSCANDO, buscando: false, error: l.error || textoError(l) }); return; }
        const { borradores, salieron } = delAnalisis(Array.isArray(l.data) ? l.data : [], analisisUrl);
        setEstado({ ...BUSCANDO, buscando: false, borradores, salieron });
      } catch (e) {
        if (vivo) setEstado({ ...BUSCANDO, buscando: false, error: textoError(leerError(e)) });
      }
    })();
    return () => { vivo = false; };
  }, [buscar, analisisUrl, intento]);

  // Con el borrador ya abierto (`idRuta`), nada de esto vale: es otra campaña.
  return {
    buscando: buscar && estado.buscando,
    /** las que ya salieron (o están programadas) con esa segmentación, de la más nueva a la más vieja: se avisan todas */
    salieron: buscar ? estado.salieron : [],
    /** los borradores de esa segmentación, mientras no se eligió «Armar otra»: se elige cuál seguir o armar otra */
    elegir: buscar && !estado.otra ? estado.borradores : [],
    /** «Armar otra»: sigue a «A quién», donde el borrador nace al tocar «Siguiente» */
    armarOtra: () => setEstado((e) => ({ ...e, otra: true })),
    error: buscar ? estado.error : "",
    reintentar: () => setIntento((n) => n + 1),
  };
}
